import { PATH_APP } from "src/routes/paths";
import QRCode from "qrcode";
import jwtDecode from "jwt-decode";

export function isValidGuid(guid) {
  const guidRegex = new RegExp(
    "^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$",
  );
  return guidRegex.test(guid);
}

export function isAdminWorking() {
  var employee = Number(localStorage.getItem("UserEmployeeId"));

  return employee === 0;
}

export function IsAdminRoute(currentPath) {
  const adminRoutes = [
    PATH_APP.tuCarta.tpv,
    PATH_APP.tuCarta.caja,
    PATH_APP.tuCarta.calendar,
    PATH_APP.tuCarta.invoices,
    PATH_APP.tuCarta.factura,
    PATH_APP.tuCarta.mesas,
    PATH_APP.tuCarta.productos,
  ];

  if (adminRoutes.includes(currentPath)) return true;

  return false;
}

export function isValidRestauranteId(idRestaurante) {
  let restaurantId;

  // Verificar y obtener el valor del campo idRestaurante si es un objeto
  if (typeof idRestaurante === "object" && idRestaurante !== null) {
    // Aquí debes verificar si el objeto tiene la propiedad idRestaurante o si el campo es el GUID directamente.
    // En este ejemplo, suponemos que el campo se llama idRestaurante.
    restaurantId = idRestaurante.idRestaurante;
  } else {
    restaurantId = idRestaurante;
  }

  // Verificar que restaurantId sea una cadena de GUID válida
  if (
    !restaurantId ||
    typeof restaurantId !== "string" ||
    !isValidGuid(restaurantId)
  ) {
    return null;
  }

  return restaurantId;
}

export const PageDefaultSize = [25, 50, 100, 200];

export const checkIfAccessTokenIsCloseToExpire = (hoursLeft) => {
  const accessToken = window.localStorage.getItem("accessToken");

  if (!accessToken) return true;

  const { exp } = jwtDecode(accessToken);
  const expirationTime = exp * 1000;
  const dateNow = new Date();

  if (expirationTime - dateNow.getTime() < hoursLeft * 60 * 60 * 1000)
    return true;

  return false;
};

export const isTheSameProductWithComplements = (product, _product) => {
  const arrA = product?.complementos ?? [];
  const arrB = _product?.complementos ?? [];
  if (arrA.length !== arrB.length) return false;
  // 2026-09-27 · Comparación de complementos ORDER-INDEPENDIENTE +
  // NULL/UNDEFINED TOLERANTE. Antes:
  //  - Se comparaba por índice → distinto orden = mismatch (fix
  //    parcial ya hecho: sort por id+notas).
  //  - `courseLabelID` con !== → undefined vs null vs 0 daban
  //    mismatch aunque semánticamente sean lo mismo ("sin curso").
  //  - `notas`/`quantity` estrictos.
  // Ahora todo normalizado. Además tolerancia FP en precio 0.005.
  const norm = (v, def = null) => (v == null ? def : v);
  const sortKey = (c) => `${c?.id ?? ""}::${(c?.notas ?? "").trim()}`;
  const a = [...arrA].sort((x, y) => sortKey(x).localeCompare(sortKey(y)));
  const b = [...arrB].sort((x, y) => sortKey(x).localeCompare(sortKey(y)));
  for (let i = 0; i < a.length; i++) {
    if (a[i].id !== b[i].id) return false;
    if ((a[i]?.notas ?? "") !== (b[i]?.notas ?? "")) return false;
    if (Math.abs((a[i].precio ?? 0) - (b[i].precio ?? 0)) > 0.005) return false;
    // courseLabelID normalizado — undefined ≡ null ≡ 0 = "sin curso".
    const cA = norm(a[i].courseLabelID, 0) || 0;
    const cB = norm(b[i].courseLabelID, 0) || 0;
    if (cA !== cB) return false;
    if ((a[i].quantity ?? 1) !== (b[i].quantity ?? 1)) return false;
  }
  return true;
};

export const isTheSameProduct = (product, _product) => {
  // 2026-09-03 · Split-by-comensal. Dos líneas del mismo producto pero
  // asignadas a comensales distintos SON líneas distintas — cada una se
  // cobra a su comensal. Normalizamos undefined/null a 0 (bucket sin
  // asignar) para que las que no llevan campo sigan mergeando entre sí.
  // 2026-09-26 · Normalización estricta: `notas` undefined vs "" y
  // `old` undefined vs false NO deben crear líneas distintas. Precio
  // con tolerancia 0.005 (2 decimales). Antes menús iguales añadidos
  // 2× desde la carta se veían como líneas separadas por diferencias
  // triviales en el shape del payload.
  const idComensalA = product.idComensal ?? 0;
  const idComensalB = _product.idComensal ?? 0;
  const notasA = product.notas ?? "";
  const notasB = _product.notas ?? "";
  const oldA = !!product.old;
  const oldB = !!_product.old;
  const precioDiff = Math.abs((product.precio ?? 0) - (_product.precio ?? 0));
  // courseLabelID normalizado — undefined ≡ null ≡ 0 (sin curso).
  const courseA = product.courseLabelID == null ? 0 : product.courseLabelID;
  const courseB = _product.courseLabelID == null ? 0 : _product.courseLabelID;

  // idComplemento normalizado — undefined/null ≡ -1 (padre del bundle).
  const idCompA = product.idComplemento == null ? -1 : product.idComplemento;
  const idCompB = _product.idComplemento == null ? -1 : _product.idComplemento;

  let isExisted =
    _product.id === product.id &&
    idCompA === idCompB &&
    notasA === notasB &&
    oldA === oldB &&
    precioDiff <= 0.005 &&
    courseA === courseB &&
    idComensalA === idComensalB &&
    // Una unidad canjeada con una promoción nunca se fusiona con otra.
    (product.loyaltyProgramId ?? null) === (_product.loyaltyProgramId ?? null) &&
    // Promociones horarias: unidades con promo y sin promo (o de otra
    // promo) nunca se fusionan, aunque coincida el precio.
    String(product.priceRuleId ?? "") === String(_product.priceRuleId ?? "");

  const productComplementosLength = product.complementos?.length ?? 0;
  const _productComplementosLength = _product.complementos?.length ?? 0;

  if (productComplementosLength !== _productComplementosLength) return false;

  if (isExisted && _productComplementosLength > 0) {
    isExisted = isTheSameProductWithComplements(product, _product);
  }

  return isExisted;
};

export const isTheSameProductForDisplays = (product, _product) => {
  return (
    isTheSameProduct(product, _product) &&
    product.unidadesEliminadas === _product.unidadesEliminadas
  );
};

export const isTheSameProductMenu = (product, _product) => {
  var isExisted =
    _product.id === product.id &&
    _product.idComplemento === product.idComplemento &&
    _product.notas === product.notas &&
    _product.old === product.old &&
    _product.precio === product.precio &&
    _product.courseLabelID === product.courseLabelID;

  return isExisted;
};

export const isTheSameProductMenuForDisplays = (product, _product) => {
  return (
    isTheSameProductMenu(product, _product) &&
    product.unidadesEliminadas === _product.unidadesEliminadas
  );
};

export const addComplementsAndFlat = (complementsA, complementsB) => {
  let combinedComplements = [...complementsA];

  complementsB.forEach((complementB) => {
    const existingComplement = combinedComplements.find(
      (complementA) =>
        complementA.id === complementB.id &&
        complementA.notas === complementB.notas &&
        complementA.precio === complementB.precio &&
        complementA.courseLabelID === complementB.courseLabelID,
    );

    if (existingComplement) {
      existingComplement.unidades += complementB.unidades;
    } else {
      combinedComplements.push({ ...complementB });
    }
  });

  return combinedComplements;
};

export const COLOR_OPTIONS = [
  "#FFECB3", // Light cream
  "#FDFEFE", // Very light grayish blue
  "#FDEBD0", // Pale peach
  "#FCF3CF", // Pastel yellow
  "#FBEEE6", // Light beige
  "#FADBD8", // Light pink
  "#FAD7A0", // Soft orange
  "#F9E79F", // Soft yellow
  "#D6EAF8", // Light blue
  "#E9F7EF", // Very light green
  "#F5B7B1", // Blush pink
  "#AED6F1", // Soft sky blue
  "#D4EFDF", // Pale green
  "#E8DAEF", // Lavender
  "#EE9B00", // Deep amber
  "#A9DFBF", // Mint green
  "#94D2BD", // Muted green
  "#CA6702", // Burnt orange
  "#6930C3", // Royal purple
  "#BB3E03", // Rustic red
  "#AE2012", // Dark crimson
  "#0A9396", // Rich aqua
  "#005F73", // Deep teal
  "#606C38", // Dark moss green
  "#4A4E69", // Slate gray
  "#5F0F40", // Rich plum
  "#023047", // Ocean blue
  "#14213D", // Midnight blue
  "#22223B", // Deep indigo
  "#283618", // Olive green
];

export const COLOR_OPTIONS_TYPEPOGRAPHY = [
  { color: "#FFECB3", textColor: "black" }, // Light cream
  { color: "#FDFEFE", textColor: "black" }, // Very light grayish blue
  { color: "#FDEBD0", textColor: "black" }, // Pale peach
  { color: "#FCF3CF", textColor: "black" }, // Pastel yellow
  { color: "#FBEEE6", textColor: "black" }, // Light beige
  { color: "#FADBD8", textColor: "black" }, // Light pink
  { color: "#FAD7A0", textColor: "black" }, // Soft orange
  { color: "#F9E79F", textColor: "black" }, // Soft yellow
  { color: "#D6EAF8", textColor: "black" }, // Light blue
  { color: "#E9F7EF", textColor: "black" }, // Very light green
  { color: "#F5B7B1", textColor: "black" }, // Blush pink
  { color: "#AED6F1", textColor: "black" }, // Soft sky blue
  { color: "#D4EFDF", textColor: "black" }, // Pale green
  { color: "#E8DAEF", textColor: "black" }, // Lavender
  { color: "#EE9B00", textColor: "black" }, // Deep amber
  { color: "#A9DFBF", textColor: "black" }, // Mint green
  { color: "#94D2BD", textColor: "black" }, // Muted green
  { color: "#CA6702", textColor: "white" }, // Burnt orange
  { color: "#6930C3", textColor: "white" }, // Royal purple
  { color: "#BB3E03", textColor: "white" }, // Rustic red
  { color: "#AE2012", textColor: "white" }, // Dark crimson
  { color: "#0A9396", textColor: "white" }, // Rich aqua
  { color: "#005F73", textColor: "white" }, // Deep teal
  { color: "#606C38", textColor: "white" }, // Dark moss green
  { color: "#4A4E69", textColor: "white" }, // Slate gray
  { color: "#5F0F40", textColor: "white" }, // Rich plum
  { color: "#023047", textColor: "white" }, // Ocean blue
  { color: "#14213D", textColor: "white" }, // Midnight blue
  { color: "#22223B", textColor: "white" }, // Deep indigo
  { color: "#283618", textColor: "white" }, // Olive green
];

// Devuelve el color de texto más legible (blanco u oscuro) sobre un fondo hex
// dado, según su luminancia percibida. Lo usan las tiles coloreadas de la
// carta del camarero (categoría/producto) para que el nombre destaque sobre el
// color de fondo en lugar de quedar ilegible (p.ej. texto oscuro sobre azul
// medianoche). Acepta #rgb, #rrggbb o null; si no es válido asume fondo claro.
export const getReadableTextColor = (hex) => {
  if (!hex || typeof hex !== "string") return "rgba(0,0,0,0.85)";
  let c = hex.replace("#", "").trim();
  if (c.length === 3)
    c = c
      .split("")
      .map((x) => x + x)
      .join("");
  if (c.length !== 6 || /[^0-9a-fA-F]/.test(c)) return "rgba(0,0,0,0.85)";
  const r = parseInt(c.slice(0, 2), 16);
  const g = parseInt(c.slice(2, 4), 16);
  const b = parseInt(c.slice(4, 6), 16);
  const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
  return luminance > 0.6 ? "rgba(0,0,0,0.87)" : "#ffffff";
};
